// XMilestoneCelebrate — Dashboard banner that surfaces the producer's
// highest UNCELEBRATED milestone for X-sharing.
//
// Why this exists: milestone events (10 / 100 / 1k / 10k loops) are
// emotionally charged and far more shareable than a routine upload.
// Right now those moments only land in the inbox — most producers
// never notice them. This banner pulls one pending milestone to the
// top of the dashboard with a one-tap "Celebrate on X" CTA. Same +1
// credit reward as a regular self-share (uses the same endpoint),
// same daily caps, but the prefilled tweet text is the punchy
// celebration line from x_follow.py:_milestone_text.
//
// Surfaces only for Producers AND only when /x/milestones/pending
// returns at least one item. Self-hides after the producer celebrates
// the surfaced item (or skips it).
import React, { useCallback, useEffect, useState } from "react";
import { Coins, ExternalLink, Loader2, PartyPopper, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import api from "../../lib/apiClient";

const XLogo = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path
      fill="currentColor"
      d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
    />
  </svg>
);

const formatThreshold = (n) => {
  if (n >= 10000) return "10,000 loops";
  if (n >= 1000) return "1,000 loops";
  if (n >= 100) return "100 loops";
  if (n >= 10) return "first 10 loops";
  return `${n} loops`;
};

const XMilestoneCelebrate = ({ isProducer, refreshUser }) => {
  const [pending, setPending] = useState([]);
  const [dismissedIds, setDismissedIds] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/x/milestones/pending");
      setPending(r.data?.pending || []);
    } catch (e) {
      // 503 (X not configured) doesn't affect THIS feature — milestone
      // celebration uses the share endpoint which works without X
      // creds. We only swallow the error and degrade silently.
      setPending([]);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (isProducer) load();
  }, [isProducer, load]);

  if (!isProducer || !loaded) return null;

  const visible = pending.filter((p) => !dismissedIds.has(`${p.video_id}:${p.threshold}`));
  if (visible.length === 0) return null;

  const next = visible[0];
  const key = `${next.video_id}:${next.threshold}`;

  const celebrate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post("/x/share/self", {
        video_id: next.video_id,
        milestone_threshold: next.threshold,
      });
      const { intent_url, reward_credits, remaining_today } = r.data || {};
      if (intent_url) window.open(intent_url, "_blank", "noopener,noreferrer");
      toast.success(
        `+${reward_credits} credit — celebration tweet ready! (${remaining_today} shares left today)`,
      );
      // Optimistically remove this entry locally; the next status fetch
      // will confirm via celebrated set.
      setDismissedIds((prev) => {
        const next_ = new Set(prev);
        next_.add(key);
        return next_;
      });
      refreshUser?.();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 429) {
        toast.info(typeof detail === "string" ? detail : "Daily share cap reached.");
        // Hide for the rest of the day to avoid nagging.
        setDismissedIds((prev) => {
          const next_ = new Set(prev);
          next_.add(key);
          return next_;
        });
      } else {
        toast.error(typeof detail === "string" ? detail : "Couldn't open the celebration tweet.");
      }
    } finally {
      setBusy(false);
    }
  };

  const skip = () => {
    setDismissedIds((prev) => {
      const n = new Set(prev);
      n.add(key);
      return n;
    });
  };

  return (
    <div
      data-testid="x-milestone-celebrate"
      className="relative mb-6 rounded-xl border border-amber-500/40 bg-gradient-to-r from-amber-950/40 via-sky-950/40 to-amber-950/40 p-4 sm:p-5 overflow-hidden"
    >
      {/* Decorative confetti hint — purely visual. */}
      <div className="absolute -top-6 -right-6 opacity-20 pointer-events-none">
        <PartyPopper className="h-24 w-24 text-amber-300" />
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 relative">
        <div className="bg-amber-500/15 border border-amber-500/40 rounded-lg p-2 shrink-0">
          <PartyPopper className="h-5 w-5 text-amber-300" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-amber-300 text-xs font-semibold uppercase tracking-wider">
            New milestone unlocked
          </p>
          <h3 className="text-white font-semibold text-base sm:text-lg leading-tight mt-0.5">
            "{next.title || "Your video"}" just crossed {formatThreshold(next.threshold)}
            {next.city ? <span className="text-gray-400 font-normal"> · {next.city}</span> : null}
          </h3>
          <p className="text-gray-400 text-xs sm:text-sm mt-1">
            Share the celebration tweet to <span className="text-white">@GlobalEyesDOTtv</span> and earn
            <span className="text-amber-300 font-semibold"> +1 credit</span>
            <span className="inline-flex items-center gap-0.5 align-middle ml-1">
              <Coins className="h-3 w-3 text-amber-400" />
            </span>
            . (Counts as today's share for this video.)
          </p>
          {visible.length > 1 && (
            <p className="text-gray-500 text-[11px] mt-1">
              {visible.length - 1} more milestone{visible.length - 1 === 1 ? "" : "s"} waiting to celebrate.
            </p>
          )}
        </div>
        <div className="flex gap-2 shrink-0">
          <Button
            onClick={celebrate}
            disabled={busy}
            data-testid="x-milestone-celebrate-btn"
            className="bg-sky-600 hover:bg-sky-700 text-white"
          >
            {busy ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : (
              <XLogo className="h-4 w-4 mr-1.5" />
            )}
            Celebrate on X
            <ExternalLink className="h-3 w-3 ml-1.5 opacity-70" />
          </Button>
          <Button
            onClick={skip}
            disabled={busy}
            variant="ghost"
            size="icon"
            data-testid="x-milestone-skip"
            className="text-gray-500 hover:text-gray-300"
            title="Skip — surface next pending milestone"
          >
            <XIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default XMilestoneCelebrate;
